import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, DownloadSimple, CheckCircle, XCircle, Trash } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { api, formatApiErrorDetail } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

export const SnapshotSection = () => {
  const [snapshots, setSnapshots] = useState([]);
  useEffect(() => {
    api.get("/admin/maintenance/snapshots").then(({ data }) => setSnapshots(data)).catch(() => {});
  }, []);

  const downloadSnapshot = async (name) => {
    try {
      const res = await fetch(`${API}/api/admin/maintenance/snapshots/${name}/download`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("Download snapshot avviato");
    } catch (err) {
      toast.error(err.message || "Download fallito");
    }
  };

  return (
    <div className="border border-surface-border rounded-lg bg-surface p-6 animate-fade-up" data-testid="snapshots-card">
      <h3 className="font-heading text-lg font-semibold tracking-tight mb-1">Snapshot di sicurezza (pre-ripristino)</h3>
      <p className="text-xs text-slate-400 mb-4">Creati in automatico prima di ogni ripristino: se il restore era sbagliato, scarica lo snapshot e ripristinalo per tornare allo stato precedente. Vengono conservati gli ultimi 5.</p>
      {snapshots.length === 0 ? (
        <p className="text-xs text-white/35" data-testid="snapshots-empty">Nessuno snapshot presente: verrà creato in automatico al prossimo ripristino.</p>
      ) : (
        <div className="space-y-2" data-testid="snapshots-list">
          {snapshots.map((s) => (
            <div key={s.name} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm border-b border-white/5 pb-2">
              <span className="text-white font-medium">{s.name}</span>
              <span className="text-slate-400">{new Date(s.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
              <span className="text-white/40 text-xs">{(s.size / 1024 / 1024).toFixed(1)} MB</span>
              <Button data-testid={`snapshot-download-${s.name}`} onClick={() => downloadSnapshot(s.name)} variant="ghost"
                className="ml-auto h-8 px-4 border border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full text-xs font-semibold">
                <DownloadSimple size={13} className="mr-1.5" /> Scarica
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ArchiveSection = () => {
  const [dailyBackups, setDailyBackups] = useState([]);
  const [dailyRunning, setDailyRunning] = useState(false);
  const [backupLog, setBackupLog] = useState([]);
  const [confirmClearLog, setConfirmClearLog] = useState(false);
  const [clearingLog, setClearingLog] = useState(false);

  const loadDailyBackups = () => {
    api.get("/admin/maintenance/daily-backups").then(({ data }) => setDailyBackups(data.backups || [])).catch(() => {});
  };
  useEffect(() => {
    loadDailyBackups();
    api.get("/admin/maintenance/backup-log").then(({ data }) => setBackupLog(data)).catch(() => {});
  }, []);

  const runDailyBackupNow = async () => {
    setDailyRunning(true);
    try {
      const { data } = await api.post("/admin/maintenance/daily-backups/run");
      toast.success(data.message);
      setTimeout(() => { loadDailyBackups(); setDailyRunning(false); }, 20000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setDailyRunning(false);
    }
  };

  const downloadDailyBackup = async (filename) => {
    try {
      const res = await fetch(`${API}/api/admin/maintenance/daily-backups/${filename}/download`, { credentials: "include" });
      if (!res.ok) throw new Error((await res.json()).detail || "Download fallito");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Download avviato");
    } catch (err) {
      toast.error(err.message || "Download fallito");
    }
  };

  const handleClearLog = async () => {
    setConfirmClearLog(false);
    setClearingLog(true);
    try {
      const { data } = await api.delete("/admin/maintenance/backup-log");
      toast.success(`Storico svuotato (${data.deleted} voci eliminate)`);
      setBackupLog([]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setClearingLog(false);
    }
  };

  return (
    <div className="border border-surface-border rounded-lg bg-surface p-6 animate-fade-up" data-testid="archive-history-card">
      <div className="flex items-center gap-2.5 mb-4">
        <Archive size={22} weight="duotone" className="text-emerald-400" />
        <h3 className="font-heading text-lg font-semibold tracking-tight">Archivio &amp; Storico</h3>
      </div>

      <div data-testid="daily-backups-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <h4 className="font-heading text-sm font-semibold tracking-tight text-white/90">Archivio backup giornalieri</h4>
          <Button data-testid="daily-backup-run-button" onClick={runDailyBackupNow} disabled={dailyRunning} variant="ghost"
            className="h-9 px-5 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 hover:text-emerald-200 rounded-full font-semibold text-xs disabled:opacity-40">
            {dailyRunning ? "Generazione in corso..." : "Genera adesso"}
          </Button>
        </div>
        <p className="text-xs text-slate-400 mb-4">Backup completo di tutte le app (Front-End + Back-End + Database; Posta AI senza dati) generato automaticamente ogni notte alle 00:00 e conservato per 7 giorni con rotazione. L'archivio viene creato sempre, anche se l'invio email fallisce. Scarica direttamente da qui.</p>
        <div className="space-y-2" data-testid="daily-backups-list">
          {dailyBackups.length === 0 && (
            <p className="text-xs text-white/35" data-testid="daily-backups-empty">Nessun backup giornaliero ancora archiviato.</p>
          )}
          {dailyBackups.map((b) => (
            <div key={b.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm border-b border-white/5 pb-2">
              <span className="text-white font-medium">{b.date}</span>
              {b.status === "ok" ? (
                <>
                  <span className="text-white/40 text-xs">{(b.size_bytes / 1024 / 1024).toFixed(1)} MB</span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-400"><CheckCircle size={12} weight="fill" /> ok</span>
                  <Button data-testid={`daily-backup-download-${b.date}`} onClick={() => downloadDailyBackup(b.filename)} variant="ghost"
                    className="ml-auto h-8 px-4 border border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full text-xs font-semibold">
                    <DownloadSimple size={13} className="mr-1.5" /> Scarica
                  </Button>
                </>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-red-400"><XCircle size={13} weight="fill" /> errore: {b.error}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-white/10" data-testid="backup-log-card">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-heading text-sm font-semibold tracking-tight text-white/90">Storico invii backup</h4>
          {backupLog.length > 0 && (
            <Button data-testid="clear-backup-log-button" onClick={() => setConfirmClearLog(true)} disabled={clearingLog} variant="ghost"
              className="h-9 px-4 border border-red-500/30 text-red-400/80 hover:bg-red-500/10 hover:text-red-400 rounded-full font-semibold text-xs">
              <Trash size={14} className="mr-1.5" /> {clearingLog ? "Svuotamento..." : "Svuota storico"}
            </Button>
          )}
        </div>
        {backupLog.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="backup-log-empty">Nessun invio registrato finora.</p>
        ) : (
          <div className="space-y-2" data-testid="backup-log-list">
            {backupLog.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm border-b border-white/5 pb-2">
                {r.ok ? (
                  <span className="flex items-center gap-1 text-emerald-400 shrink-0"><CheckCircle size={14} weight="fill" /> inviato</span>
                ) : (
                  <span className="flex items-center gap-1 text-red-400 shrink-0"><XCircle size={14} weight="fill" /> fallito</span>
                )}
                <span className="text-slate-400 shrink-0">
                  {new Date(r.timestamp).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
                <span className="text-white/60 text-xs uppercase tracking-wider shrink-0">{r.kind}</span>
                {r.filename && <span className="text-white font-medium shrink-0">{r.filename}</span>}
                <span className="text-slate-400 truncate">→ {(r.recipients || []).join(", ")}</span>
                {r.error && <span className="text-red-400/80 text-xs truncate max-w-[320px]" title={r.error}>{r.error}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={confirmClearLog} onOpenChange={setConfirmClearLog}>
        <AlertDialogContent className="bg-surface border-white/10 text-white" data-testid="clear-log-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Svuotare lo storico?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Tutte le voci dello storico invii backup verranno eliminate definitivamente. I backup già inviati via email non vengono toccati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-log-cancel-button" className="bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white rounded-full">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction data-testid="clear-log-confirm-button" onClick={handleClearLog}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full">
              Svuota storico
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
